import { ENDPOINT_SCM } from '../config'
import type {
  CreateBindingRequest,
  ScmBranches,
  ScmIdentity,
  ScmInstallation,
  ScmRepoBinding,
  ScmRepository,
  StartConnectRequest,
} from '../types'
import AuthAxios from '@/lib/api/axios/AuthAxios'

const base = (workspaceSlug: string) => `${ENDPOINT_SCM}/${workspaceSlug}/scm`

/** Begin the GitHub connect handshake — returns the GitHub authorize URL to redirect to. */
export const startScmConnect = async (
  workspaceSlug: string,
  request: StartConnectRequest = {},
): Promise<string> => {
  const response = await AuthAxios.post<{ url: string }>(`${base(workspaceSlug)}/connect/start`, request)
  return response.data.url
}

/** List this workspace's connected installations. */
export const listScmInstallations = async (workspaceSlug: string): Promise<Array<ScmInstallation>> => {
  const response = await AuthAxios.get<{ installations: Array<ScmInstallation> }>(`${base(workspaceSlug)}/installations`)
  return response.data.installations
}

/** List an installation's repositories (for the picker). */
export const listScmRepositories = async (
  workspaceSlug: string,
  installationId: string,
): Promise<Array<ScmRepository>> => {
  const response = await AuthAxios.get<{ repositories: Array<ScmRepository> }>(
    `${base(workspaceSlug)}/repositories`,
    { params: { installationId } },
  )
  return response.data.repositories
}

/** List a bound repo's branches (+ its default) for the base-branch picker. */
export const listScmBranches = async (
  workspaceSlug: string,
  installationId: string,
  repo: string,
): Promise<ScmBranches> => {
  const response = await AuthAxios.get<ScmBranches>(
    `${base(workspaceSlug)}/branches`,
    { params: { installationId, repo } },
  )
  return response.data
}

/** List GitHub-login → member mappings for this workspace (PR author attribution). */
export const listScmIdentities = async (workspaceSlug: string): Promise<Array<ScmIdentity>> => {
  const response = await AuthAxios.get<{ identities: Array<ScmIdentity> }>(`${base(workspaceSlug)}/identities`)
  return response.data.identities
}

/** List this workspace's repo→project bindings. */
export const listScmBindings = async (workspaceSlug: string): Promise<Array<ScmRepoBinding>> => {
  const response = await AuthAxios.get<{ bindings: Array<ScmRepoBinding> }>(`${base(workspaceSlug)}/bindings`)
  return response.data.bindings
}

/** Bind a repository to a project (one repo → at most one project). */
export const createScmBinding = async (
  workspaceSlug: string,
  request: CreateBindingRequest,
): Promise<void> => {
  await AuthAxios.post(`${base(workspaceSlug)}/bindings`, request)
}

/** Remove a repo→project binding (PR history is unaffected). */
export const deleteScmBinding = async (workspaceSlug: string, bindingId: string): Promise<void> => {
  await AuthAxios.delete(`${base(workspaceSlug)}/bindings/${bindingId}`)
}
